/* Appendix A.7: shared by CommonJS Nest and Vite. Pure metadata, no browser or secrets. */
const ORIGIN = 'https://skytechsolutions.co.ke';
const DEFAULT_IMAGE = `${ORIGIN}/icons/icon-512.png`;
const decode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};
const privateRoute =
  /^\/(cart|checkout|wishlist|login|forgot-password|reset-password|verify-email|orders)(\/|$)/;
const flattenCategories = (rows) =>
  rows.flatMap((row) => [row, ...flattenCategories(row.children || [])]);
const publicImage = (image) =>
  image && /^\/uploads\//.test(image.path)
    ? `${ORIGIN}${image.path.replace(/\/?$/, '/')}${image.displayOrder}-large.webp`
    : null;
function buildSeo({ path = '/', product, categories = [], unavailable = false }) {
  const url = new URL(path, ORIGIN);
  const pathname = url.pathname.replace(/\/+$/, '') || '/';
  const params = url.searchParams;
  const productRoute = /^\/products\/([^/]+)$/.test(pathname);
  const categoryPath = /^\/category\/([^/]+)$/.exec(pathname)?.[1];
  const categorySlug = categoryPath
    ? decode(categoryPath)
    : pathname === '/products'
      ? params.get('category')
      : null;
  const category = flattenCategories(categories).find((c) => c.slug === categorySlug);
  const home = pathname === '/';
  const catalog = pathname === '/products';
  const policyTitles = {
    '/privacy': 'Privacy Policy',
    '/delivery': 'Delivery & pickup',
    '/returns': 'Returns & refunds',
    '/terms': 'Terms & conditions',
  };
  const privacy = Boolean(policyTitles[pathname]);
  const missing =
    unavailable ||
    (productRoute && !product) ||
    (Boolean(categorySlug) && !category) ||
    (!home && !catalog && !category && !productRoute && !privacy && !privateRoute.test(pathname));
  const status = missing ? 404 : 200;
  const productUrl = product ? `${ORIGIN}/products/${encodeURIComponent(product.slug)}` : null;
  const canonical =
    productUrl ||
    (category ? `${ORIGIN}/category/${encodeURIComponent(category.slug)}` : `${ORIGIN}${pathname}`);
  // Tracking parameters don't create a different document; functional filters/search do.
  const filtered = [...params.keys()].some(
    (k) =>
      !['category', 'gclid', 'dclid', 'fbclid', 'msclkid'].includes(k) && !k.startsWith('utm_'),
  );
  const emptyCategory = category && category.totalProductCount === 0;
  const indexable = !missing && !privateRoute.test(pathname) && !filtered && !emptyCategory;
  const title = missing
    ? 'Page not found | SkyTech Kenya'
    : product
      ? product.metaTitle?.trim() || `${product.name} | SkyTech Kenya`
      : category
        ? `${category.name} in Kenya | SkyTech Solutions`
        : catalog
          ? 'CCTV, Networking & Electronics in Kenya | SkyTech Solutions'
          : home
            ? 'SkyTech Solutions — CCTV, Networking & Electronics in Nairobi'
            : privacy
              ? `${policyTitles[pathname]} | SkyTech Solutions`
              : 'SkyTech Solutions';
  const policyDescriptions = {
    '/delivery':
      'Nairobi rider delivery, nationwide matatu and bus parcel collection, current zone fees and Philadelphia House pickup. Confirm the destination office before dispatch.',
    '/returns':
      'Read SkyTech’s two-day routine return window, receipt and good-condition requirements, customer-paid return delivery and one-working-day refund processing.',
    '/terms':
      'Read SkyTech Solutions purchase terms, payment information, delivery, returns, product warranties and consumer rights.',
  };
  const description = missing
    ? 'This page is unavailable. Browse the current SkyTech product catalog.'
    : product
      ? product.metaDescription?.trim() ||
        `Shop ${product.name}${product.brand ? ` by ${product.brand}` : ''} at SkyTech Kenya. View specifications, current KES pricing, availability and delivery options.`
      : category
        ? `Shop ${category.name} in Kenya. Compare specifications, availability and KES prices at SkyTech Solutions.`
        : policyDescriptions[pathname] ||
          'Explore CCTV cameras, networking equipment and electronics from SkyTech Solutions in Nairobi, Kenya.';
  const images = (product?.images || []).map(publicImage).filter(Boolean);
  const organization = {
    '@type': 'Organization',
    '@id': `${ORIGIN}/#organization`,
    name: 'SkyTech Solutions',
    url: ORIGIN,
    logo: `${ORIGIN}/icons/icon-512.png`,
  };
  const graph = [organization];
  if (home)
    graph.push({
      '@type': 'WebSite',
      '@id': `${ORIGIN}/#website`,
      url: ORIGIN,
      name: 'SkyTech Solutions',
      inLanguage: 'en-KE',
      publisher: { '@id': organization['@id'] },
      // Appendix A.7: describe the existing catalog search, without promising a rich result.
      potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${ORIGIN}/products?q={search_term_string}` },
        'query-input': 'required name=search_term_string',
      },
    });
  if (product && !missing) {
    graph.push({
      '@type': 'Product',
      '@id': `${productUrl}#product`,
      name: product.name,
      sku: product.sku,
      description,
      ...(images.length ? { image: images } : {}),
      ...(product.brand ? { brand: { '@type': 'Brand', name: product.brand } } : {}),
      offers: {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'KES',
        price: product.price,
        availability:
          product.stockQuantity > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        seller: { '@id': organization['@id'] },
      },
      ...(product.reviewCount > 0 && product.averageRating > 0
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: product.averageRating,
              reviewCount: product.reviewCount,
            },
          }
        : {}),
    });
  }
  if ((product || category || catalog) && !missing)
    graph.push({
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Products', item: `${ORIGIN}/products` },
        ...(product || category
          ? [
              {
                '@type': 'ListItem',
                position: 3,
                name: product?.name || category.name,
                item: canonical,
              },
            ]
          : []),
      ],
    });
  return {
    status,
    title,
    description,
    canonical,
    robots: indexable ? 'index,follow' : 'noindex,follow',
    image: images[0] || DEFAULT_IMAGE,
    type: product && !missing ? 'product' : 'website',
    schema: { '@context': 'https://schema.org', '@graph': graph },
  };
}
const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
  );
const serializeSeo = (value) => JSON.stringify(value).replace(/</g, '\\u003c');
function renderSeoHead(doc) {
  const meta = (key, value, property = false) =>
    `<meta ${property ? 'property' : 'name'}="${key}" content="${escapeHtml(value)}">`;
  return (
    `<title>${escapeHtml(doc.title)}</title>` +
    meta('description', doc.description) +
    meta('robots', doc.robots) +
    `<link rel="canonical" href="${escapeHtml(doc.canonical)}">` +
    ['title', 'description', 'url', 'type', 'image', 'locale']
      .map((key) => meta(`og:${key}`, { url: doc.canonical, locale: 'en_KE', ...doc }[key], true))
      .join('') +
    meta('twitter:card', 'summary_large_image') +
    meta('twitter:title', doc.title) +
    meta('twitter:description', doc.description) +
    meta('twitter:image', doc.image) +
    `<script id="skytech-structured-data" type="application/ld+json">${serializeSeo(doc.schema)}</script>`
  );
}
function injectSeoHead(shell, doc) {
  // Only the build template is processed. User-authored strings are escaped afterward.
  const clean = shell
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, '')
    .replace(
      /<meta\b[^>]*(?:name|property)\s*=\s*["'](?:description|robots|og:[^"']+|twitter:[^"']+)["'][^>]*>/gi,
      '',
    )
    .replace(/<link\b[^>]*rel\s*=\s*["']canonical["'][^>]*>/gi, '')
    .replace(/<script\b[^>]*id=["']skytech-structured-data["'][^>]*>[\s\S]*?<\/script>/gi, '');
  return clean.replace(/<\/head>/i, () => renderSeoHead(doc) + '</head>');
}
module.exports = { ORIGIN, buildSeo, renderSeoHead, injectSeoHead, flattenCategories };
